import React, { useState } from "react";
import axios from "axios";
import { X, Send, Check, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "donor", label: "Donor / Funder" },
  { id: "non_profit", label: "Non-Profit" },
  { id: "consulting", label: "Consulting agency" },
  { id: "other", label: "Other" },
];

export default function BookSessionModal({ open, onClose, source = "landing", accent = "maroon" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("donor");
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  const accentColor = accent === "orange" ? "#E67E22" : "#8B1A1A";
  const accentDark = accent === "orange" ? "#C66810" : "#6B1414";

  if (!open) return null;

  const reset = () => {
    setName(""); setEmail(""); setRole("donor");
    setState({ loading: false, success: false, error: "" });
  };

  const handleClose = () => {
    onClose?.();
    setTimeout(reset, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: "" });
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        role,
        source,
      });
      if (data?.ok) {
        setState({ loading: false, success: true, error: "" });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't send right now. Please try again or email partnerships@dhwaniris.com.";
      setState({ loading: false, success: false, error: msg });
    }
  };

  return (
    <div className="bsm-backdrop" onClick={handleClose} data-testid="book-session-modal">
      <div className="bsm-card" onClick={(e) => e.stopPropagation()}>
        <button className="bsm-close" onClick={handleClose} aria-label="Close" data-testid="book-session-close">
          <X size={18} />
        </button>

        {state.success ? (
          <div className="bsm-success" data-testid="book-session-success">
            <div className="bsm-success-icon" style={{ background: accentColor }}>
              <Check size={28} strokeWidth={3} />
            </div>
            <h3>Request sent.</h3>
            <p>
              Thanks, {name.split(" ")[0] || "there"}. The mGrant team has been notified and
              we’ve also copied <strong>{email}</strong> so you have a record. Someone will reach
              out shortly.
            </p>
            <button className="bsm-btn" onClick={handleClose} style={{ background: accentColor }} data-testid="book-session-done-btn">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bsm-form">
            <div className="bsm-head">
              <div className="bsm-eyebrow" style={{ color: accentColor }}>
                <span className="bsm-dot" style={{ background: accentColor }} /> Book a session
              </div>
              <h3>Let's talk about your grant workflow.</h3>
              <p>Leave your details — a short intro is enough. We'll reach out within one working day.</p>
            </div>

            <label className="bsm-field">
              <span className="bsm-label">Your name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
                data-testid="book-session-name"
              />
            </label>

            <label className="bsm-field">
              <span className="bsm-label">Work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organisation.org"
                required
                data-testid="book-session-email"
              />
            </label>

            <div className="bsm-field">
              <span className="bsm-label">I represent a…</span>
              <div className="bsm-role-grid" role="radiogroup" aria-label="Role">
                {ROLES.map((r) => {
                  const active = role === r.id;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      role="radio"
                      aria-checked={active}
                      onClick={() => setRole(r.id)}
                      className={`bsm-role ${active ? "is-active" : ""}`}
                      style={active ? { borderColor: accentColor, color: accentColor, background: `${accentColor}0D` } : {}}
                      data-testid={`book-session-role-${r.id}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {state.error ? (
              <div className="bsm-error" data-testid="book-session-error">{state.error}</div>
            ) : null}

            <button
              type="submit"
              className="bsm-btn"
              disabled={state.loading}
              style={{ background: accentColor }}
              onMouseOver={(e) => (e.currentTarget.style.background = accentDark)}
              onMouseOut={(e) => (e.currentTarget.style.background = accentColor)}
              data-testid="book-session-submit"
            >
              {state.loading ? (
                <>
                  <Loader2 className="bsm-spin" size={16} /> Sending…
                </>
              ) : (
                <>
                  Send request <Send size={15} />
                </>
              )}
            </button>

            <div className="bsm-foot">
              <Mail size={12} /> We'll also CC you at the email above.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
